import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { Eye, EyeOff } from "lucide-react";
import * as React from "react"
import { changePassword } from "@/lib/api/user"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Icons } from "@/components/icons"

const formSchema = z
  .object({
    old_password: z.string().min(1, "Old password is required"),
    new_password: z.string().min(8, "Password must be at least 8 characters"),
    confirm_password: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  })

export function ChangePasswordForm() {
  const { toast } = useToast()
  const navigate = useNavigate()

  const [isCurrentPasswordVisible, setIsCurrentPasswordVisible] =
    React.useState<boolean>(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] =
    React.useState<boolean>(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    React.useState<boolean>(false);

  const toggleCurrentPasswordVisibility = () =>
    setIsCurrentPasswordVisible((prevState) => !prevState);

  const toggleNewPasswordVisibility = () =>
    setIsNewPasswordVisible((prevState) => !prevState);

  const toggleConfirmPasswordVisibility = () =>
    setIsConfirmPasswordVisible((prevState) => !prevState);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      old_password: "",
      new_password: "",
      confirm_password: "",
    },
  })

  const mutation = useMutation({
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      const data = {
        old_password: values.old_password,
        current_password: values.old_password,
        new_password: values.new_password,
      }
      await changePassword(data)
    },
    onSuccess: () => {
      toast({
        description: "Password updated successfully.",
      })
      navigate({ to: "/settings/profile" })
    },
    onError: () => {
      toast({
        title: "Old password is wrong!",
        description: "Please try again.",
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="old_password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current Password</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input

                    type={isCurrentPasswordVisible ? "text" : "password"}
                    {...field}
                  />
                  <button
                    className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 ring-offset-background transition-shadow hover:text-gray-900 focus-visible:border focus-visible:border-ring focus-visible:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                    type="button"
                    onClick={toggleCurrentPasswordVisibility}
                    aria-label={
                      isCurrentPasswordVisible
                        ? "Hide password"
                        : "Show password"
                    }
                    aria-pressed={isCurrentPasswordVisible}
                    aria-controls="password"
                  >
                    {isCurrentPasswordVisible ? (
                      <EyeOff size={16} strokeWidth={2} aria-hidden="true" />
                    ) : (
                      <Eye size={16} strokeWidth={2} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="new_password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New Password</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type={isNewPasswordVisible ? "text" : "password"}
                    {...field}
                  />
                  <button
                    className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 ring-offset-background transition-shadow hover:text-gray-900 focus-visible:border focus-visible:border-ring focus-visible:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                    type="button"
                    onClick={toggleNewPasswordVisibility}
                    aria-label={
                      isNewPasswordVisible ? "Hide password" : "Show password"
                    }
                    aria-pressed={isNewPasswordVisible}
                    aria-controls="password"
                  >
                    {isNewPasswordVisible ? (
                      <EyeOff size={16} strokeWidth={2} aria-hidden="true" />
                    ) : (
                      <Eye size={16} strokeWidth={2} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirm_password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm New Password</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input

                    type={isConfirmPasswordVisible ? "text" : "password"}
                    {...field}
                  />
                  <button
                    className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 ring-offset-background transition-shadow hover:text-gray-900 focus-visible:border focus-visible:border-ring focus-visible:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    aria-label={
                      isConfirmPasswordVisible
                        ? "Hide password"
                        : "Show password"
                    }
                    aria-pressed={isConfirmPasswordVisible}
                    aria-controls="password"
                  >
                    {isConfirmPasswordVisible ? (
                      <EyeOff size={16} strokeWidth={2} aria-hidden="true" />
                    ) : (
                      <Eye size={16} strokeWidth={2} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Button
            disabled={mutation.isPending}
            type="submit"
            className="w-full md:w-auto"
          >
            {mutation.isPending && (
              <Icons.loader className="mr-2 animate-spin" />
            )}
            Change Password
          </Button>
        </div>
      </form>
    </Form>
  )
}
